import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Dropdown from 'react-bootstrap/Dropdown';
import {
  useBudgetMutation,
  useLinkedFringesMutation,
} from 'utils/api/mutations';
import { formatCurrency } from 'utils/helper';
import CreateFringe from 'components/CreateFringe';
import { toast } from 'react-toastify';
import { useFringeQuery } from 'utils/api/queries';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Chip from '@mui/material/Chip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

import './styles.css';

// Fringe-first shape: fringeGroups[fringe_id][link_group] = rowIds
function getLinkGroupForRow(fringeGroups, fringeId, rowId) {
  const groups = fringeGroups[fringeId];
  if (!groups) return null;
  const entry = Object.entries(groups).find(([_, ids]) =>
    ids.includes(String(rowId))
  );
  return entry ? entry[0] : null;
}

function isRowInLinkedGroup(fringeGroups, fringeId, rowId) {
  return Object.values(fringeGroups[fringeId] || {}).some(ids =>
    ids.includes(String(rowId))
  );
}

const CHIP_TEXT_COLOR = 'var(--cbp-text-strong, #000000)';
const CHIP_BORDER_COLOR = 'var(--cbp-border-strong, #ced4da)';
const CHIP_MUTED_BACKGROUND = 'var(--cbp-surface-muted, #f0f0f0)';
const CHIP_HIGHLIGHT_TEXT_COLOR = '#ffffff';

function removeRowFromFringeGroups(fringeGroups, fringeId, linkGroupId, rowId) {
  const next = { ...fringeGroups };
  if (!next[fringeId]) return next;
  next[fringeId] = { ...next[fringeId] };
  const ids = (next[fringeId][linkGroupId] || []).filter(
    id => id !== String(rowId)
  );
  if (ids.length === 0) {
    delete next[fringeId][linkGroupId];
    if (Object.keys(next[fringeId]).length === 0) delete next[fringeId];
  } else {
    next[fringeId][linkGroupId] = ids;
  }
  return next;
}

function MultiSelectDropdown({
  groupFringeData,
  menuOptions,
  selectedValues,
  onChange,
  className,
  toggleClass,
  menuClass,
  toggleIcon,
  ItemRow,
  focus,
  fringeRowCount,
  setShowCheckboxColumn,
  setSelectedFringe,
  setShowModal,
  highlightedRows,
  rowId,
  fringeGroups,
  hoveredFringeId,
  setHoveredFringeId,
  hoveredRowId,
  setHoveredRowId,
  selectedFringe,
  setFringeGroups,
  refreshLinkedFringes,
  detailSheetType,
}) {
  const [show, setShow] = useState(false);
  const [hoveredChip, setHoveredChip] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const hoverTimeout = useRef(null);
  const [showCreateFringeModal, setShowCreateFringeModal] = useState(false);
  const [combinedOptions, setCombinedOptions] = useState([]);
  const [showPopover, setShowPopover] = useState(null);
  const hideTimeoutRef = useRef(null);
  const popoverRef = useRef(null);
  const currentHoverId = useRef(null);
  const { activeBudget } = useHomeScreenContext();
  const overlayContainer =
    typeof document !== 'undefined' ? document.body : undefined;
  const fixedPopperConfig = useMemo(
    () => ({
      strategy: 'fixed',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
      ],
    }),
    []
  );

  const handleFringeCreated = (newFringe, res) => {
    const newFringeOption = {
      id: `fringe-${res.data.data.id}`,
      value: newFringe.abbreviation,
      label: newFringe.abbreviation,
      type: 'fringe',
      display_name: newFringe.display_name,
    };

    const updatedSelection = [...selectedValues, newFringeOption];

    onChange(updatedSelection);

    // setCombinedOptions(prev => [...prev, newFringeOption]);

    setShowCreateFringeModal(false);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setShowPopover(null); // Close the popover
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverRef]);

  const { refetch } = useFringeQuery.useFringeDetailsQuery({
    filters: { instance_type: 'budget', active_id: activeBudget },
    enabled: true,
  });

  useEffect(() => {
    setHoveredChip(null);
    setHoveredFringeId(null); // Reset hovered fringe when focus changes
  }, [focus, setHoveredFringeId]);

  const { mutateAsync: fetchHoverBudget } =
    useBudgetMutation.useHoverBudgetMutation();

  useEffect(() => {
    const newCombinedOptions = [
      // Group options are already normalized (e.g. id: "group-2")
      ...groupFringeData.map(group => ({
        ...group,
      })),
      // Fringe options are already normalized (e.g. id: "fringe-5")
      ...menuOptions.map(fringe => ({
        ...fringe,
      })),
    ];
    setCombinedOptions(newCombinedOptions);
  }, [groupFringeData, menuOptions]);

  const closeFolder = () => {
    setShowCreateFringeModal(false);
    refetch();
  };

  const handlePopoverToggle = id => {
    setShowPopover(prevId => (prevId === id ? null : id));
    const selectedOption = selectedValues.find(opt => opt.id === id);
    if (setSelectedFringe && selectedOption) {
      setSelectedFringe(selectedOption);
    }
  };

  const handleToggle = useCallback(nextShow => {
    setShow(nextShow);
  }, []);
  const { mutateAsync: unlinkFringeAPI } =
    useLinkedFringesMutation.useUnlinkFringesMutation();

  const handleSelect = useCallback(
    async (eventKey, event) => {
      event?.stopPropagation();

      // Handle the "Add Fringe" button
      if (eventKey === 'navigate-somewhere') {
        setShowCreateFringeModal(true);
        return;
      }

      const selectedOption = combinedOptions.find(opt => opt.id === eventKey);

      if (!selectedOption) return;

      const { id, type } = selectedOption;
      const isGroup = type === 'group';
      const alreadySelected = selectedValues.some(item => item.id === id);

      if (alreadySelected) {
        // For plain fringes, keep existing unlink logic (linked groups etc.)
        // For groups, let the detail-sheet update handle removal; no unlink API call
        if (!isGroup && rowId) {
          const isLinkedToCurrentRow = Object.values(fringeGroups).some(group =>
            Object.entries(group).some(([fringeId, rowIds]) => {
              return fringeId === id && rowIds.includes(String(rowId));
            })
          );

          try {
            if (isLinkedToCurrentRow) {
              const linkGroupId = getLinkGroupForRow(fringeGroups, id, rowId);

              if (linkGroupId) {
                await unlinkFringeAPI({
                  fringe_id: id,
                  detail_sheet_type: detailSheetType,
                  link_group: linkGroupId,
                  row_id: rowId,
                });

                const updatedFringeGroups = removeRowFromFringeGroups(
                  fringeGroups,
                  id,
                  linkGroupId,
                  rowId
                );
                setFringeGroups(updatedFringeGroups);
              } else {
                await unlinkFringeAPI({
                  fringe_id: id,
                  detail_sheet_type: detailSheetType,
                  row_id: rowId,
                });
              }
            } else {
              // Not in a link group; still remove the assignment from this row
              await unlinkFringeAPI({
                fringe_id: id,
                detail_sheet_type: detailSheetType,
                row_id: rowId,
              });
              // toast.success('Fringe unlinked successfully.');
            }
          } catch (error) {
            console.error('Error unlinking fringe:', error);
            toast.error('Failed to unlink fringe.');
            return; // Stop further execution if unlinking fails
          }
        }
      }

      // Update the selected values
      const updatedSelection = alreadySelected
        ? selectedValues.filter(item => item.id !== id)
        : [...selectedValues, selectedOption];

      onChange(updatedSelection);
    },
    [
      selectedValues,
      onChange,
      combinedOptions,
      detailSheetType,
      fringeGroups,
      rowId,
      unlinkFringeAPI,
      setFringeGroups,
      setShowCreateFringeModal,
    ]
  );

  // create mutation for unlinking fringe

  const handleRemove = async id => {
    const selectedOption = selectedValues.find(item => item.id === id);
    const isGroup = selectedOption?.type === 'group';

    const isLinkedToCurrentRow =
      !isGroup && isRowInLinkedGroup(fringeGroups, id, rowId);

    try {
      if (!isGroup && rowId) {
        if (isLinkedToCurrentRow) {
          const linkGroupId = getLinkGroupForRow(fringeGroups, id, rowId);

          if (!linkGroupId) {
            await unlinkFringeAPI({
              fringe_id: id,
              detail_sheet_type: detailSheetType,
              row_id: rowId,
            });
          } else {
            await unlinkFringeAPI({
              fringe_id: id,
              detail_sheet_type: detailSheetType,
              link_group: linkGroupId,
              row_id: rowId,
            });

            const updatedFringeGroups = removeRowFromFringeGroups(
              fringeGroups,
              id,
              linkGroupId,
              rowId
            );
            setFringeGroups(updatedFringeGroups);
          }
        } else {
          await unlinkFringeAPI({
            fringe_id: id,
            detail_sheet_type: detailSheetType,
            row_id: rowId,
          });
        }
      }
    } catch (error) {
      console.error('Error removing fringe:', error);
      toast.error('Failed to remove fringe.');
    }
    const updatedSelection = selectedValues.filter(item => item.id !== id);
    onChange(updatedSelection);
    // If the removed fringe was currently selected, clear it from Redux
    if (selectedFringe?.id === id && typeof setSelectedFringe === 'function') {
      setSelectedFringe(null);
      refreshLinkedFringes();
    }
    setHoveredChip(null);
    setHoveredFringeId(null);
  };

  const handleMouseOver = useCallback(
    (id, event) => {
      event.stopPropagation();

      // Don't show the hover popover when hovering over the delete icon (3-dot / close button)
      const target = event.target;
      const isOverDeleteIcon =
        target.closest('.MuiChip-deleteIcon') ||
        target.classList.contains('MuiChip-deleteIcon');
      if (isOverDeleteIcon) {
        // Clear any pending hover popover
        if (hoverTimeout.current?.timeout) {
          clearTimeout(hoverTimeout.current.timeout);
        }
        if (hoverTimeout.current?.abort) {
          hoverTimeout.current.abort();
        }
        currentHoverId.current = null;
        setHoveredChip(null);
        return;
      }

      const triggerRect = event.currentTarget.getBoundingClientRect();
      const popupWidth = 280;
      const viewportWidth = window.innerWidth || 0;
      const fallbackLeft = Math.max(12, triggerRect.left - popupWidth - 8);
      const preferredRightEdge = triggerRect.right + popupWidth + 8;
      const popupPositionForTrigger = {
        top: Math.max(12, triggerRect.top - 16),
        left:
          viewportWidth && preferredRightEdge > viewportWidth
            ? fallbackLeft
            : triggerRect.right + 8,
      };

      // Clear any existing hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // Set the hovered fringe ID
      setHoveredFringeId(id);
      setHoveredRowId(rowId);

      if (hoverTimeout.current?.abort) {
        hoverTimeout.current.abort();
      }
      if (hoverTimeout.current?.timeout) {
        clearTimeout(hoverTimeout.current.timeout);
      }

      const controller = new AbortController();
      hoverTimeout.current = controller;

      currentHoverId.current = id;

      hoverTimeout.current.timeout = setTimeout(() => {
        currentHoverId.current = id;
        setHoveredChip({ id, fringe_total: null });

        (async () => {
          try {
            const selectedOption = combinedOptions.find(opt => opt.id === id);
            if (!selectedOption) return;

            const { type } = selectedOption;
            const hoverData = await fetchHoverBudget({
              item_total: ItemRow.item_total,
              fringe: selectedOption.value,
              type,
              signal: controller.signal,
              id: selectedOption.id,
              row_id: rowId,
            });

            if (currentHoverId.current !== id) return;

            if (!hoverData?.data?.fringe_total) {
              console.warn(
                `No fringe_total found for: ${selectedOption.value}`
              );
              return;
            }

            setPopupPosition(popupPositionForTrigger);
            setHoveredChip({ id, fringe_total: hoverData.data.fringe_total });
          } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('Fetch error:', error);
          }
        })();
      }, 300);
    },
    [
      ItemRow.item_total,
      fetchHoverBudget,
      setHoveredFringeId,
      setHoveredRowId,
      combinedOptions,
      rowId,
    ]
  );

  const handleMouseOut = (id, event) => {
    if (hoverTimeout.current?.abort) {
      hoverTimeout.current.abort();
    }
    if (hoverTimeout.current?.timeout) {
      clearTimeout(hoverTimeout.current.timeout);
    }

    // Clear any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Set a new timeout to hide the popover after 100ms
    hideTimeoutRef.current = setTimeout(() => {
      currentHoverId.current = null;
      setHoveredChip(null);
      setHoveredFringeId(null);
    }, 100); // 100ms delay
  };

  // const isFringeHighlighted = useCallback(
  //   fringeId => {
  //     if (!hoveredFringeId || fringeId !== hoveredFringeId) return false;

  //     let hoveredRowIds = [];
  //     Object.values(fringeGroups).forEach(fringes => {
  //       if (fringes[hoveredFringeId]) {
  //         hoveredRowIds = fringes[hoveredFringeId]; // e.g., ["rowid1", "rowid2"]
  //       }
  //     });

  //     return hoveredRowIds.includes(String(rowId));
  //   },
  //   [hoveredFringeId, fringeGroups, rowId]
  // );

  // const isFringeHighlighted = useCallback(
  //   fringeId => {
  //     if (!hoveredFringeId || fringeId !== hoveredFringeId) return false;

  //     // Check if the hovered fringe is linked to the hoveredRowId
  //     let linkedRowIds = [];
  //     let isLinkedToHoveredRow = false;
  //     Object.values(fringeGroups).forEach(fringes => {
  //       if (fringes[hoveredFringeId]) {
  //         linkedRowIds = fringes[hoveredFringeId]; // e.g., ["row1", "row2"]
  //         if (linkedRowIds.includes(String(hoveredRowId))) {
  //           isLinkedToHoveredRow = true; // Fringe is linked to the hovered row
  //         }
  //       }
  //     });

  //     // Only highlight if the fringe is linked to the hoveredRowId and the current rowId is in linked rows
  //     return isLinkedToHoveredRow && linkedRowIds.includes(String(rowId));
  //   },
  //   [hoveredFringeId, hoveredRowId, fringeGroups, rowId]
  // );

  const isFringeHighlighted = useCallback(
    fringeId => {
      if (!hoveredFringeId || fringeId !== hoveredFringeId) return false;
      return isRowInLinkedGroup(fringeGroups, fringeId, rowId);
    },
    [hoveredFringeId, fringeGroups, rowId]
  );
  const selectedChips = (() => {
    const maxVisibleChips = 2; // Maximum number of chips to display before showing "+X"

    if (!focus && selectedValues.length > 0) {
      const visibleChips = selectedValues.slice(0, maxVisibleChips); // Get the first N chips
      const remainingCount = selectedValues.length - visibleChips.length; // Calculate remaining chips

      return [
        ...visibleChips.map((chip, index) => {
          const isHovered = hoveredChip?.id === chip.id;
          const isAppliedToMultipleRows = (fringeRowCount[chip.label] || 0) > 1;
          const isHighlighted = isFringeHighlighted(chip.id);
          const isLinkedToCurrentRow = isRowInLinkedGroup(
            fringeGroups,
            chip.id,
            rowId
          );
          const chipBorderColor = isLinkedToCurrentRow
            ? chip.color
            : CHIP_BORDER_COLOR;

          return (
            <OverlayTrigger
              key={chip.id}
              trigger="click"
              placement="right"
              show={showPopover === chip.id}
              onToggle={() => setShowPopover(null)}
              container={overlayContainer}
              popperConfig={fixedPopperConfig}
              rootClose
              overlay={
                <Popover id={`popover-${chip.id}`} ref={popoverRef}>
                  <Popover.Body
                    style={{
                      padding: '0',
                      margin: '0',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '200px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <div
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: '#fff',
                        fontSize: '14px',
                        color: '#000',
                      }}
                      onClick={() => {
                        setShowModal(true);
                        setSelectedFringe(chip);
                        setShowPopover(null);
                      }}
                      onMouseEnter={e =>
                        (e.target.style.backgroundColor = '#f5f5f5')
                      }
                      onMouseLeave={e =>
                        (e.target.style.backgroundColor = '#fff')
                      }
                    >
                      {isLinkedToCurrentRow
                        ? `Link/Unlink ${
                            selectedFringe?.type === 'group'
                              ? 'Group'
                              : 'Fringe'
                          }`
                        : `Link to Other ${
                            selectedFringe?.type === 'group'
                              ? 'Group'
                              : 'Fringe'
                          }`}
                    </div>

                    <div
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: '#fff',
                        fontSize: '14px',
                        color: '#BF0000',
                      }}
                      onClick={() => {
                        handleRemove(chip.id);
                        setShowPopover(null);
                      }}
                      onMouseEnter={e =>
                        (e.target.style.backgroundColor = '#f5f5f5')
                      }
                      onMouseLeave={e =>
                        (e.target.style.backgroundColor = '#fff')
                      }
                    >
                      {isLinkedToCurrentRow
                        ? `Remove ${
                            selectedFringe?.type === 'group'
                              ? 'Group'
                              : 'Fringe'
                          }`
                        : `Remove ${
                            selectedFringe?.type === 'group'
                              ? 'Group'
                              : 'Fringe'
                          }`}
                    </div>
                  </Popover.Body>
                </Popover>
              }
            >
              <Chip
                label={chip.label}
                onDelete={
                  isAppliedToMultipleRows
                    ? () => handlePopoverToggle(chip.id)
                    : () => handleRemove(chip.id)
                }
                deleteIcon={
                  isAppliedToMultipleRows ? (
                    <MoreVertIcon
                      sx={{
                        color: isHovered
                          ? '#4285F4 !important'
                          : '#757575 !important',
                      }}
                    />
                  ) : (
                    <CloseIcon
                      sx={{
                        color: isHovered
                          ? '#BF0000 !important'
                          : '#757575 !important',
                      }}
                    />
                  )
                }
                className="chip"
                style={{
                  color: isHighlighted
                    ? CHIP_HIGHLIGHT_TEXT_COLOR
                    : CHIP_TEXT_COLOR,
                  backgroundColor: isHighlighted
                    ? chipBorderColor
                    : 'transparent',
                  border: isLinkedToCurrentRow
                    ? `2px solid ${chipBorderColor}`
                    : `1px solid ${CHIP_BORDER_COLOR}`,
                  borderRadius: '7px',
                  marginRight: '4px',
                  height: '28px',
                }}
                onMouseOver={e => handleMouseOver(chip.id, e)}
                onMouseOut={e => handleMouseOut(chip.id, e)}
              />
            </OverlayTrigger>
          );
        }),
        remainingCount > 0 && (
          <Chip
            key="more"
            label={`+${remainingCount}`}
            className="chip"
            style={{
              backgroundColor: CHIP_MUTED_BACKGROUND,
              color: CHIP_TEXT_COLOR,
              border: `1px solid ${CHIP_BORDER_COLOR}`,
              borderRadius: '16px',
              height: '28px',
            }}
          />
        ),
      ];
    }

    return selectedValues.map(({ id, label, display_name, color }) => {
      const isAppliedToMultipleRows = (fringeRowCount[label] || 0) > 1;
      const isHovered = hoveredChip?.id === id;
      const isHighlighted = isFringeHighlighted(id);

      const isLinkedToCurrentRow = isRowInLinkedGroup(fringeGroups, id, rowId);

      const chipBorderColor = isLinkedToCurrentRow ? color : CHIP_BORDER_COLOR;

      return (
        <OverlayTrigger
          key={id}
          trigger="click"
          placement="right"
          show={showPopover === id}
          onToggle={() => setShowPopover(null)}
          container={overlayContainer}
          popperConfig={fixedPopperConfig}
          rootClose
          overlay={
            <Popover id={`popover-${id}`} ref={popoverRef}>
              <Popover.Body
                style={{
                  padding: '0',
                  margin: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '200px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
              >
                <div
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    color: '#000',
                  }}
                  onClick={() => {
                    setShowModal(true);
                    setSelectedFringe({ id, label, display_name, color });
                    setShowPopover(null);
                  }}
                  onMouseEnter={e =>
                    (e.target.style.backgroundColor = '#f5f5f5')
                  }
                  onMouseLeave={e => (e.target.style.backgroundColor = '#fff')}
                >
                  {isLinkedToCurrentRow
                    ? `Link/Unlink ${
                        selectedFringe?.type === 'group' ? 'Group' : 'Fringe'
                      }`
                    : `Link to Other ${
                        selectedFringe?.type === 'group' ? 'Group' : 'Fringe'
                      }`}
                </div>

                <div
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    color: '#BF0000',
                  }}
                  onClick={() => {
                    handleRemove(id);
                    setShowPopover(null);
                  }}
                  onMouseEnter={e =>
                    (e.target.style.backgroundColor = '#f5f5f5')
                  }
                  onMouseLeave={e => (e.target.style.backgroundColor = '#fff')}
                >
                  {isLinkedToCurrentRow
                    ? `Remove ${
                        selectedFringe?.type === 'group' ? 'Group' : 'Fringe'
                      }`
                    : `Remove ${
                        selectedFringe?.type === 'group' ? 'Group' : 'Fringe'
                      }`}
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <Chip
            label={focus ? display_name : label}
            onDelete={
              focus
                ? isAppliedToMultipleRows
                  ? () => handlePopoverToggle(id)
                  : () => handleRemove(id)
                : undefined
            }
            deleteIcon={
              focus ? (
                isAppliedToMultipleRows ? (
                  <MoreVertIcon
                    sx={{
                      color: isHovered
                        ? '#4285F4 !important'
                        : '#757575 !important',
                    }}
                  />
                ) : (
                  <CloseIcon
                    sx={{
                      color: isHovered
                        ? '#BF0000 !important'
                        : '#757575 !important',
                    }}
                  />
                )
              ) : null
            }
            className="chip"
            style={{
              color: isHighlighted
                ? CHIP_HIGHLIGHT_TEXT_COLOR
                : CHIP_TEXT_COLOR,
              backgroundColor: isHighlighted ? chipBorderColor : 'transparent',
              border: isLinkedToCurrentRow
                ? `2px solid ${chipBorderColor}`
                : `1px solid ${CHIP_BORDER_COLOR}`,
              borderRadius: '7px',
            }}
            onMouseOver={e => handleMouseOver(id, e)}
            onMouseOut={e => handleMouseOut(id, e)}
          />
        </OverlayTrigger>
      );
    });
  })();

  const formatDecimalValue = (value, type = null) => {
    if (!value) {
      return '-';
    }

    if (type === 'percentage') {
      return `${value.toFixed(2)}%`;
    }
    return formatCurrency(value.toFixed(2));
  };

  return (
    <>
      <Dropdown
        className={`my-multi-dropdown ${
          selectedValues.length === 0 ? 'my-multi-dropdown-empty' : ''
        } ${className}`}
        show={show}
        onToggle={handleToggle}
        onSelect={handleSelect}
        autoClose="outside"
        // style={{ width: focus ? 'auto' : '12rem' }} // Apply fixed width when contracted
      >
        <Dropdown.Toggle
          className={`${toggleClass} toggle-button-centered`}
          style={{
            gap: selectedValues.length > 0 ? '4px' : '0px', // Adjust gap dynamically
          }}
          onClick={() => {
            if (menuOptions.length === 0) {
              setShowCreateFringeModal(true); // Open Create Fringe modal
            } else {
              setShow(prevShow => !prevShow); // Toggle dropdown
            }
          }}
        >
          <div className={`selected-chips ${focus ? 'expanded' : ''}`}>
            {selectedChips}
          </div>
          <div className="add-icon-wrapper">+</div>
        </Dropdown.Toggle>

        <Dropdown.Menu className={`my-multi-dropdown-menu ${menuClass}`}>
          {combinedOptions.map(option => {
            if (option.value === 'divider') return null;

            return (
              <Dropdown.Item
                key={option.id}
                eventKey={option.id}
                active={selectedValues.some(item => item.id === option.id)}
                as="div"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px',
                  overflow: 'hidden',
                  opacity: 1,
                  cursor: 'pointer', // Allow selection even if linked
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.some(item => item.id === option.id)}
                  readOnly
                  className="checkbox-style"
                  style={{
                    width: '14px',
                    height: '14px',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '14px' }}>{option.display_name}</span>
              </Dropdown.Item>
            );
          })}
          <Dropdown.Item
            as="button"
            eventKey="navigate-somewhere"
            className="dropdown-add-item"
            style={{ fontWeight: 'bold' }}
          >
            <span
              className="plus-icon"
              style={{ position: 'relative', right: '3px', fontSize: '20px' }}
            >
              +
            </span>
            <span style={{ marginLeft: '2px', color: '#4285f4' }}>
              Add Fringe
            </span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      {hoveredChip &&
        popupPosition &&
        hoveredChip.fringe_total &&
        typeof hoveredChip.fringe_total === 'object' &&
        overlayContainer &&
        createPortal(
          <div
            ref={popoverRef}
            className="chip-popup"
            style={{
              position: 'fixed',
              top: popupPosition.top,
              left: popupPosition.left,
              background: '#ffffff',
              border: '1px solid #ddd',
              borderRadius: '5px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 12000,
              fontSize: '12px',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              textAlign: 'left',
              maxWidth: '400px',
              padding: '8px',
              transition: 'opacity 0.1s ease-in-out',
            }}
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }}
            onMouseLeave={() => {
              hideTimeoutRef.current = setTimeout(() => {
                setHoveredChip(null);
                setHoveredFringeId(null);
              }, 100);
            }}
          >
            <div>
              {hoveredChip.fringe_total.type === 'group' ? (
                <>
                  <strong style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    Fringe Group Name:
                  </strong>{' '}
                  {hoveredChip.fringe_total.name}
                  <br />
                  <br />
                  {Object.entries(
                    hoveredChip.fringe_total.fringe_breakdown || {}
                  ).map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong>{' '}
                      {formatCurrency(value?.toFixed(2))}
                    </div>
                  ))}
                  <br />
                  <strong>Fringe Group Total:</strong>{' '}
                  {formatCurrency(
                    hoveredChip.fringe_total.fringe_total?.toFixed(2)
                  )}
                </>
              ) : (
                <>
                  <strong>Fringe Full Name:</strong>{' '}
                  {hoveredChip.fringe_total.name}
                  <br />
                  <strong>Fringe Name:</strong>{' '}
                  {hoveredChip.fringe_total.display_name}
                  <br />
                  <strong>Fringe Total:</strong>{' '}
                  {formatDecimalValue(hoveredChip.fringe_total.fringe_total)}
                  <br />
                  <strong>Fringe Value:</strong>{' '}
                  {formatDecimalValue(
                    hoveredChip.fringe_total.value,
                    hoveredChip.fringe_total.fringe_type
                  )}
                  <br />
                  <strong>Fringe Type:</strong>{' '}
                  {hoveredChip.fringe_total.fringe_type}
                  <br />
                  <strong>Fringe Base Cap:</strong>{' '}
                  {formatDecimalValue(hoveredChip.fringe_total.base_cap)}
                  <br />
                  <strong>Fringe Cap On Total:</strong>{' '}
                  {formatDecimalValue(hoveredChip.fringe_total.cap_on_total)}
                  <br />
                  {hoveredChip.fringe_total.linked_group_total ? (
                    <>
                      <strong>Linked Fringe Total: </strong>
                      {formatCurrency(
                        hoveredChip.fringe_total.linked_group_total?.toFixed(2)
                      )}
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>,
          overlayContainer
        )}
      {showCreateFringeModal && (
        <CreateFringe
          closeFolder={closeFolder}
          showCreateFringeModal={showCreateFringeModal}
          refetch={refetch}
          onFringeCreated={handleFringeCreated}
        />
      )}
    </>
  );
}

// PropTypes and defaultProps remain unchanged
MultiSelectDropdown.propTypes = {
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
    })
  ).isRequired,
  groupFringeData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
      color: PropTypes.string,
    })
  ),
  selectedValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
      color: PropTypes.string,
    })
  ),
  onChange: PropTypes.func,
  className: PropTypes.string,
  toggleClass: PropTypes.string,
  menuClass: PropTypes.string,
  toggleIcon: PropTypes.node,
  ItemRow: PropTypes.shape({
    item_total: PropTypes.number.isRequired,
  }).isRequired,
  focus: PropTypes.bool,
  fringeRowCount: PropTypes.object,
  setShowCheckboxColumn: PropTypes.func,
  setSelectedFringe: PropTypes.func,
  setShowModal: PropTypes.func,
  hoverData: PropTypes.object,
  highlightedRows: PropTypes.array,
  rowId: PropTypes.string,
  fringeGroups: PropTypes.object,
  hoveredFringeId: PropTypes.string,
  setHoveredFringeId: PropTypes.func,
  hoveredRowId: PropTypes.string,
  setHoveredRowId: PropTypes.func,
  selectedFringe: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    display_name: PropTypes.string,
    color: PropTypes.string,
  }),
  setFringeGroups: PropTypes.func,
  refreshLinkedFringes: PropTypes.func.isRequired,
  detailSheetType: PropTypes.string,
};

MultiSelectDropdown.defaultProps = {
  groupFringeData: [],
  selectedValues: [],
  onChange: () => null,
  className: '',
  toggleClass: '',
  menuClass: '',
  toggleIcon: null,
  focus: false,
  fringeRowCount: {},
  setShowCheckboxColumn: () => null,
  setSelectedFringe: () => null,
  setShowModal: () => null,
  hoverData: {},
  highlightedRows: [],
  rowId: '',
  fringeGroups: {},
  hoveredFringeId: null,
  setHoveredFringeId: () => null,
  hoveredRowId: null,
  setHoveredRowId: () => null,
  selectedFringe: () => null,
  setFringeGroups: () => null,
  detailSheetType: 'swimlane',
};

export default MultiSelectDropdown;
